/**
 * My Accounts (TMMAPP-0008, design §12.7 / §14.4 row 3; the v2 page, TMMAPP-0151 §D, prototype state
 * 30, P:455–487) at `/me/accounts`. The header shows "Net worth" with its figure, **Ask about this**
 * (every account the person owns) and **Add** (each kind of account, and Connect a bank). Six
 * sections follow, each an `h2` with its served total at the end: **Cash**, **Credit cards & lines**,
 * **Shared with you** (accounts granted to the person; no total is served, so none shows), **Real
 * estate & vehicles**, **Investments** and **Loans**. A section with nothing in it isn't shown; a
 * person with nothing at all sees the Cash section's first-account add card. Each shown section ends
 * in its add card. The category tiles that filtered one grid are gone. Under the sections: the cash
 * identity note. Nothing here appears in a virtual account (MM-D-43). A GUEST sees the empty layout
 * and its add cards, which open the purchase experience. A lapsed owner (`NONE` with accounts) sees
 * the page read-only, with the same add cards opening purchase.
 *
 * TMMAPP-0120:
 * - "How should we help?" opens on arrival for a subscriber who owns no account (once per visit,
 *   never over another dialog, unless "Don't show this again" was ticked on this browser); the
 *   header's `meta` line has a link that opens it any time, and "{n} rows need allocation" while
 *   n > 0 (the Allocation card is gone).
 * - The Bank connections section is gone: the cash and revolving add cards open a "New account" menu
 *   (add manually, or connect a bank through the consent gate); each bank-linked card shows its
 *   connection status and that connection's actions; a connection with unmapped accounts shows a
 *   "Finish setting up {bank}" banner above the tiles.
 * - Every grid is a `SortableCardGrid` (pattern 15): the owner's open accounts / active holdings,
 *   with the add card last. Hidden unmanaged accounts keep their place in the full id list sent.
 */
import { useCallback, useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Link,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  Typography,
  useTheme,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shell/NotifyProvider';
import { accountsApi, type Account, type AccountType } from '@/api/accounts';
import { connectorsApi, type ConnectorAccount, type ConnectorItem } from '@/api/connectors';
import { ErrorCodes, isApiError } from '@/api/errors';
import { holdingsApi, type Holding, type HoldingType } from '@/api/holdings';
import { netWorthApi, type NetWorth } from '@/api/netWorth';
import { reviewApi } from '@/api/review';
import { summaryApi, type Summary } from '@/api/summary';
import { DisposeHoldingDialog } from '@/features/holdings/DisposeHoldingDialog';
import { HoldingEditorDialog } from '@/features/holdings/HoldingEditorDialog';
import { NetWorthCard } from '@/features/holdings/NetWorthCard';
import { AskAboutThis } from '@/features/assistant/AskAboutThis';
import { MoneyParts } from '@/components/MoneyText';
import { ValuationDialog } from '@/features/holdings/ValuationDialog';
import { SubscriberActionHint, useSubscriberAction } from '@/session/useSubscriberAction';
import { DeletionPendingAlert } from '@/session/DeletionPendingAlert';
import { useMe } from '@/session/meHooks';
import { useSubscribeDialogOpen } from '@/features/subscribe/subscribeOfferContext';
import { ObjectMenu } from '@/shell/OverflowMenu';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { booleanParam, useUrlState } from '@/shared/useUrlState';
import { ConnectionStatus, type ConnectionActions } from '@/features/connections/ConnectionStatus';
import { MapAccountsDialog } from '@/features/connections/MapAccountsDialog';
import { MapExistingDialog } from '@/features/connections/MapExistingDialog';
import { useBankConnect, type MapTarget } from '@/features/connections/useBankConnect';
import { HowWeHelpDialog } from '@/features/automation/HowWeHelpDialog';
import { readHowWeHelpHidden } from '@/features/automation/howWeHelpHidden';
import { useAutomationGate } from '@/features/automation/useAutomationGate';

import { AddNewCard } from '@/components/AddNewCard';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ErrorState } from '@/components/ErrorState';
import { SortableCardGrid } from '@/components/SortableCardGrid';
import { useConfirm } from '@/components/useConfirm';
import { ACCOUNT_MIN_CARD_WIDTH, cardGridSx } from '@/components/cardGrid';
import { resolveTokens } from '@/theme/theme';
import { AccountFormDialog } from './AccountFormDialog';
import {
  AccountCard,
  HoldingCard,
  type AccountCardActions,
  type HoldingCardActions,
} from './AccountCards';
import { canImportBatches, isOwnerAccount } from './accountAccess';
import { mergeHiddenOrder } from './mergeHiddenOrder';
import { readShowUnmanaged, writeShowUnmanaged } from './unmanagedStyle';
import { unallocatedHref } from './unallocatedHref';
import { proposalReviewCount } from './useInstitutionAnswer';

const SKELETON_KEYS = ['a', 'b', 'c', 'd', 'e'] as const;
/** After a queued sync, Sync now waits this long (as the connections section did). */
const SYNC_GUARD_MS = 60_000;
// `add` and `connect` are one-shot entry links: used once, then cleared from the URL (replace), so
// Back or a reload does not reopen the dialog or the connect flow.
const URL_SCHEMA = {
  add: booleanParam(),
  connect: booleanParam(),
};

/** The six sections, in the prototype's order (P:1242). */
type SectionKey = 'cash' | 'revolving' | 'shared' | 'assets' | 'tradable' | 'loans';

/** The owed totals are served positive; Credit cards & lines and Loans read negative. */
function owed(minor: number): number {
  return 0 - Math.abs(minor);
}

type PageState =
  | { status: 'loading' }
  | { status: 'ready'; accounts: Account[]; summary: Summary }
  | { status: 'error'; error: unknown };

type Block<T> =
  { status: 'loading' } | { status: 'ready'; data: T } | { status: 'error'; error: unknown };

/** The failed block's error, for a section's `ErrorState` (undefined while loading or ready). */
function blockError(block: Block<unknown>): unknown {
  return block.status === 'error' ? block.error : undefined;
}

type DialogState =
  | { kind: 'create'; type?: AccountType }
  | { kind: 'edit'; account: Account; focusOpening?: boolean }
  | { kind: 'holding-create'; type: HoldingType }
  | { kind: 'holding-edit'; holding: Holding }
  | { kind: 'holding-value'; holding: Holding }
  | { kind: 'holding-dispose'; holding: Holding }
  | null;

/** Why "How should we help?" is open: on arrival, from the header link, or gating a bank connect. */
type HelpState = 'arrival' | 'link' | 'connect' | null;

type ReorderableType = Extract<AccountType, 'CASH' | 'REVOLVING' | 'LOAN'>;

function operatingOf(summary: Summary, meCurrency: string | null): string | null {
  return summary.operatingCurrency ?? meCurrency;
}

/** tmm-api's reorder set for an account type: the owner's open accounts of it. */
function inAccountSet(account: Account): boolean {
  return isOwnerAccount(account) && account.status === 'OPEN';
}

/** tmm-api's reorder set for a holding type: the active ones. */
function inHoldingSet(holding: Holding): boolean {
  return holding.status === 'ACTIVE';
}

/**
 * `rows` with the members of the reorder set in `ids`' order, in the positions those members held;
 * everything else stays where it was (the optimistic paint before the reorder call answers).
 */
function withOrder<T extends { id: number }>(
  rows: readonly T[],
  member: (row: T) => boolean,
  ids: readonly number[],
): T[] {
  const byId = new Map(rows.map((row) => [row.id, row]));
  const ordered = ids.flatMap((id) => {
    const row = byId.get(id);
    return row !== undefined && member(row) ? [row] : [];
  });
  let next = 0;
  return rows.map((row) => (member(row) && next < ordered.length ? ordered[next++]! : row));
}

export function AccountsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const theme = useTheme();
  const guest = me.tier === 'GUEST';
  const gate = useAutomationGate(!guest);
  const tokens = resolveTokens(theme);
  const navigate = useNavigate();
  const subscribeOpen = useSubscribeDialogOpen();
  const [{ add: addLink, connect: connectLink }, setUrl] = useUrlState(URL_SCHEMA);
  const [state, setState] = useState<PageState>({ status: 'loading' });
  const [connectors, setConnectors] = useState<ConnectorItem[]>([]);
  const [syncUntil, setSyncUntil] = useState<Record<number, number>>({});
  const [help, setHelp] = useState<HelpState>(null);
  const [addMenu, setAddMenu] = useState<{ anchor: HTMLElement; type: AccountType } | null>(null);
  const [holdingsBlock, setHoldingsBlock] = useState<Block<Holding[]>>({ status: 'loading' });
  const [netWorthBlock, setNetWorthBlock] = useState<Block<NetWorth>>({ status: 'loading' });
  // Each own account's "{n} to review" (TMMAPP-0159 §F 33): the served `byAccount`, never derived,
  // plus a standing institution proposal (TMMAPP-0165 item 18). Null until read and after a failed
  // read: no chips.
  const [reviewCounts, setReviewCounts] = useState<ReadonlyMap<number, number> | null>(null);
  const [dialog, setDialog] = useState<DialogState>(null);
  const [addAnyMenu, setAddAnyMenu] = useState<HTMLElement | null>(null);
  const [allocMenu, setAllocMenu] = useState<HTMLElement | null>(null);
  const [closing, setClosing] = useState<Account | null>(null);
  const [closeBusy, setCloseBusy] = useState(false);
  const [closeError, setCloseError] = useState<string | null>(null);
  const notify = useNotify();
  const [mapTarget, setMapTarget] = useState<MapTarget | null>(null);
  const [showUnmanaged, setShowUnmanaged] = useState(readShowUnmanaged);
  const [showDisposed, setShowDisposed] = useState(false);
  const disconnecting = useConfirm<ConnectorItem>();
  const started = useRef(false);
  // One counter per grid (TMMAPP-0122 item 7): a holdings reorder never voids an accounts rollback.
  const reorderTicket = useRef(0);
  const holdingsReorderTicket = useRef(0);
  /** "How should we help?" opened (or was declined) once on this visit: it never reopens on its own. */
  const arrivalShown = useRef(false);
  const title = usePageTitle({ title: t('toolsNav.accounts') });

  const subscriber = me.tier === 'SUBSCRIBER';
  const addCash = useSubscriberAction((event) =>
    setAddMenu({ anchor: event.currentTarget, type: 'CASH' }),
  );
  const addRevolving = useSubscriberAction((event) =>
    setAddMenu({ anchor: event.currentTarget, type: 'REVOLVING' }),
  );
  const addLoan = useSubscriberAction(() => setDialog({ kind: 'create', type: 'LOAN' }));
  const addAsset = useSubscriberAction(() =>
    setDialog({ kind: 'holding-create', type: 'FIXED_ASSET' }),
  );
  const addTradable = useSubscriberAction(() =>
    setDialog({ kind: 'holding-create', type: 'TRADABLE' }),
  );
  const addAny = useSubscriberAction((event) => setAddAnyMenu(event.currentTarget));
  const helpLink = useSubscriberAction(() => {
    arrivalShown.current = true;
    if (gate.catalog === null) {
      if (!gate.loading) void navigate('/me/automation-help');
      return;
    }
    setHelp('link');
  });

  const loadHoldings = useCallback(async () => {
    try {
      setHoldingsBlock({ status: 'ready', data: await holdingsApi.list() });
    } catch (error) {
      setHoldingsBlock({ status: 'error', error });
    }
  }, []);

  const loadNetWorth = useCallback(async () => {
    try {
      setNetWorthBlock({ status: 'ready', data: await netWorthApi.get() });
    } catch (error) {
      setNetWorthBlock({ status: 'error', error });
    }
  }, []);

  /** The cards' review chips; a subscriber's own queue only. A failed read shows no chip. */
  const loadReviewCounts = useCallback(async () => {
    if (me.tier !== 'SUBSCRIBER') return;
    try {
      const summary = await reviewApi.summary();
      setReviewCounts(new Map(summary.byAccount.map((row) => [row.accountId, row.total])));
    } catch {
      setReviewCounts(null);
    }
  }, [me.tier]);

  /** The connections behind the cards' statuses; unreadable or unavailable means no statuses. */
  const loadConnectors = useCallback(async () => {
    try {
      const list = await connectorsApi.list();
      setConnectors(list.filter((item) => item.status !== 'REMOVED'));
    } catch {
      setConnectors([]);
    }
  }, []);

  const load = useCallback(async () => {
    const holdingsP = loadHoldings();
    const netWorthP = loadNetWorth();
    const connectorsP = loadConnectors();
    const reviewP = loadReviewCounts();
    const [accountsResult, summaryResult] = await Promise.allSettled([
      accountsApi.list(),
      summaryApi.get(),
    ]);
    await Promise.all([holdingsP, netWorthP, connectorsP, reviewP]);
    if (accountsResult.status === 'rejected' || summaryResult.status === 'rejected') {
      const failed = accountsResult.status === 'rejected' ? accountsResult : summaryResult;
      setState({
        status: 'error',
        error: failed.status === 'rejected' ? failed.reason : undefined,
      });
      return;
    }
    setState({ status: 'ready', accounts: accountsResult.value, summary: summaryResult.value });
  }, [loadHoldings, loadNetWorth, loadConnectors, loadReviewCounts]);

  const connect = useBankConnect({
    onConnected: (outcome, target) => {
      void load();
      if (outcome === 'needsMapping') {
        notify(t('connections.needsMapping'), {
          ...(target !== undefined
            ? {
                action: {
                  label: t('connections.mapNow'),
                  onClick: () => setMapTarget(target),
                },
              }
            : {}),
        });
        return;
      }
      notify(
        outcome === 'reconnected' ? t('connections.reconnected') : t('connections.syncQueued'),
      );
    },
    onStale: () => void loadConnectors(),
  });

  const connectError = connect.sessionExpired
    ? t('connections.sessionNotFound')
    : connect.notReauthable
      ? t('connections.notReauthable')
      : connect.institutionMismatch
        ? t('connections.institutionMismatch')
        : connect.status === 'error'
          ? connect.errorMessage !== null && connect.errorMessage.length > 0
            ? t('connections.linkFailed', { message: connect.errorMessage })
            : t('connections.linkFailedGeneric')
          : null;
  const announcedConnect = useRef<string | null>(null);
  useEffect(() => {
    if (connectError === null) {
      announcedConnect.current = null;
      return;
    }
    if (announcedConnect.current === connectError) return;
    announcedConnect.current = connectError;
    notify(connectError, { severity: 'error' });
  }, [connectError, notify]);

  useEffect(() => {
    if (guest) return;
    if (started.current) return;
    started.current = true;
    void load();
  }, [guest, load]);

  /** Connect a bank: through "How should we help?" while the choices are unreviewed. */
  const startConnect = useCallback(() => {
    if (gate.loading) return;
    arrivalShown.current = true;
    if (gate.needsConsent) {
      if (gate.catalog === null) {
        void navigate('/me/automation-help');
        return;
      }
      setHelp('connect');
      return;
    }
    void connect.start();
  }, [gate.loading, gate.needsConsent, gate.catalog, connect, navigate]);

  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (guest || state.status !== 'ready' || deepLinkHandled.current || gate.loading) return;
    // A one-shot link is this visit's first dialog: the arrival dialog (whose effect runs in the
    // same flush, below) stands down, so the two never race or stack.
    if (connectLink || addLink) arrivalShown.current = true;
    if (connectLink) {
      deepLinkHandled.current = true;
      setUrl({ connect: false, add: false });
      queueMicrotask(startConnect);
      return;
    }
    if (addLink) {
      deepLinkHandled.current = true;
      setUrl({ add: false });
      queueMicrotask(() => setDialog({ kind: 'create' }));
    }
  }, [guest, state.status, addLink, connectLink, setUrl, startConnect, gate.loading]);

  // "How should we help?" on arrival (item 3): a subscriber who owns no account, once per visit,
  // after the page has rendered, and never over another dialog. The flag is spent when it opens, or
  // when "Don't show this again" says it won't.
  const ownsNoAccount =
    state.status === 'ready' && !state.accounts.some((account) => isOwnerAccount(account));
  const otherDialogOpen =
    subscribeOpen ||
    help !== null ||
    dialog !== null ||
    closing !== null ||
    mapTarget !== null ||
    connect.mapping !== null ||
    disconnecting.target !== null;
  useEffect(() => {
    if (!subscriber || arrivalShown.current || !ownsNoAccount) return;
    if (gate.catalog === null || otherDialogOpen) return;
    arrivalShown.current = true;
    if (readHowWeHelpHidden(me.user.id)) return;
    queueMicrotask(() => setHelp('arrival'));
  }, [subscriber, ownsNoAccount, gate.catalog, otherDialogOpen, me.user.id]);

  const page: PageState = guest
    ? {
        status: 'ready',
        accounts: [],
        summary: {
          cash: [],
          operatingCurrency: me.user.currency,
          allocatedMinor: 0,
          unallocatedMinor: 0,
          statementBalanceMinor: 0,
          unallocatedLineCount: 0,
          nextPlanned: null,
          openingPendingAccountIds: [],
        },
      }
    : state;
  const holdingsView: Block<Holding[]> = guest ? { status: 'ready', data: [] } : holdingsBlock;
  const netWorthView: Block<NetWorth> = guest
    ? {
        status: 'ready',
        data: {
          asOf: null,
          valuedAsOf: null,
          cashMinor: 0,
          revolvingMinor: 0,
          loansMinor: 0,
          fixedAssetsMinor: 0,
          tradableMinor: 0,
          netWorthMinor: 0,
          cash: [],
          revolving: [],
          loans: [],
          fixedAssets: [],
          tradable: [],
          series: [],
          unmanagedIncluded: false,
          openingPendingAccountIds: [],
        },
      }
    : netWorthBlock;

  // Every state renders the same `PageHeader` first, so the `h1` (and route focus on it) survives
  // loading → ready / error.
  const ownedScope =
    page.status === 'ready'
      ? page.accounts
          .filter(isOwnerAccount)
          .map((account) => ({ kind: 'ACCOUNT' as const, id: account.id }))
      : null;
  const header = (netWorth: ReactNode, meta?: ReactNode) => (
    <PageHeader
      title={title}
      subtitle={t('accounts.pageSubtitle')}
      actions={
        <>
          {netWorth}
          <AskAboutThis name={t('askAbout.nameAccounts')} scope={guest ? [] : ownedScope} />
          <Button
            variant="outlined"
            aria-haspopup="menu"
            aria-expanded={addAnyMenu !== null}
            aria-describedby={addAny.descriptionId}
            onClick={addAny.run}
          >
            {t('accounts.addMenu')}
          </Button>
          <SubscriberActionHint id={addAny.descriptionId} description={addAny.description} />
        </>
      }
      meta={meta}
    />
  );

  if (page.status === 'loading') {
    return (
      <>
        {header(null)}
        <Box sx={cardGridSx(undefined, 160)} aria-hidden>
          {SKELETON_KEYS.map((k) => (
            <Skeleton key={k} variant="rounded" height={88} />
          ))}
        </Box>
      </>
    );
  }

  if (page.status === 'error') {
    return (
      <>
        {header(null)}
        <ErrorState
          error={page.error}
          what={t('errors.what.accounts')}
          onRetry={() => {
            setState({ status: 'loading' });
            started.current = false;
            void load();
          }}
        />
      </>
    );
  }

  const { accounts, summary } = page;
  const operating = operatingOf(summary, me.user.currency);
  const timeZone = me.user.timezone ?? 'UTC';
  const unmanagedCount = accounts.filter((a) => a.management === 'UNMANAGED').length;
  const visibleAccounts = showUnmanaged
    ? accounts
    : accounts.filter((a) => a.management !== 'UNMANAGED');
  const cash = visibleAccounts.filter((a) => a.type === 'CASH');
  const revolving = visibleAccounts.filter((a) => a.type === 'REVOLVING');
  const loans = visibleAccounts.filter((a) => a.type === 'LOAN');
  const firstAccount = subscriber && accounts.length === 0;
  const owner = subscriber;
  const netWorth = netWorthView.status === 'ready' ? netWorthView.data : null;
  const holdings = holdingsView.status === 'ready' ? holdingsView.data : null;
  const connectionByAccount = new Map<number, ConnectorItem>();
  for (const item of connectors) {
    for (const row of item.accounts) {
      if (row.accountId !== null) connectionByAccount.set(row.accountId, item);
    }
  }
  const unmapped: { item: ConnectorItem; account: ConnectorAccount }[] = owner
    ? connectors.flatMap((item) => {
        const first = item.accounts.find((row) => row.accountId === null);
        return first !== undefined ? [{ item, account: first }] : [];
      })
    : [];
  const unallocatedCount = summary.unallocatedLineCount;

  function handleShowUnmanaged(checked: boolean) {
    setShowUnmanaged(checked);
    writeShowUnmanaged(checked);
  }

  function openUnallocated(account: Account) {
    setAllocMenu(null);
    void navigate(unallocatedHref(account.id, account.budgetStartOn));
  }

  function handleNeedsClick(event: MouseEvent<HTMLElement>) {
    if (cash.length === 0) return;
    if (cash.length === 1) {
      openUnallocated(cash[0]!);
      return;
    }
    setAllocMenu(event.currentTarget);
  }

  function reorderFailed(error: unknown) {
    if (isApiError(error) && error.code === ErrorCodes.REORDER_SET_MISMATCH) {
      notify(t('rules.reorderMismatch'), { severity: 'error' });
      void load();
    } else if (isApiError(error) && error.code === ErrorCodes.USER_FROZEN) {
      notify(t('errors.userFrozen'), { severity: 'error' });
    } else if (isApiError(error) && error.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
      notify(t('errors.subscribe'), { severity: 'error' });
    } else {
      notify(t('accounts.errorGeneric'), { severity: 'error' });
    }
  }

  async function reorderAccounts(type: ReorderableType, ids: Array<string | number>) {
    if (state.status !== 'ready') return;
    const previous = state;
    const member = (account: Account) => account.type === type && inAccountSet(account);
    // The grid sends the cards it shows; tmm-api wants the type's whole set, hidden ones included.
    const full = mergeHiddenOrder(
      previous.accounts.filter(member).map((account) => account.id),
      ids.map(Number),
    );
    const ticket = ++reorderTicket.current;
    setState({ ...previous, accounts: withOrder(previous.accounts, member, full) });
    try {
      await accountsApi.reorder(type, full);
      if (ticket !== reorderTicket.current) return;
      await load();
    } catch (error) {
      if (ticket !== reorderTicket.current) return;
      setState(previous);
      reorderFailed(error);
      throw error;
    }
  }

  async function reorderHoldings(type: HoldingType, ids: Array<string | number>) {
    if (holdingsBlock.status !== 'ready') return;
    const previous = holdingsBlock;
    const member = (holding: Holding) => holding.type === type && inHoldingSet(holding);
    const full = mergeHiddenOrder(
      previous.data.filter(member).map((holding) => holding.id),
      ids.map(Number),
    );
    const ticket = ++holdingsReorderTicket.current;
    setHoldingsBlock({ status: 'ready', data: withOrder(previous.data, member, full) });
    try {
      await holdingsApi.reorder(type, full);
      if (ticket !== holdingsReorderTicket.current) return;
      await loadHoldings();
    } catch (error) {
      if (ticket !== holdingsReorderTicket.current) return;
      setHoldingsBlock(previous);
      reorderFailed(error);
      throw error;
    }
  }

  async function handleSync(item: ConnectorItem) {
    try {
      await connectorsApi.sync(item.id);
      setSyncUntil((prev) => ({ ...prev, [item.id]: Date.now() + SYNC_GUARD_MS }));
      notify(t('connections.syncQueued'));
    } catch (error) {
      notify(
        isApiError(error) && error.code === ErrorCodes.CONNECTOR_UNAVAILABLE
          ? t('connections.unavailable')
          : t('accounts.errorGeneric'),
        { severity: 'error' },
      );
    }
  }

  async function handleDisconnect() {
    await disconnecting.run(
      async (item) => {
        await connectorsApi.remove(item.id);
        notify(t('connections.disconnected'));
        await load();
      },
      (error) =>
        isApiError(error) && error.code === ErrorCodes.CONNECTOR_UNAVAILABLE
          ? t('connections.unavailable')
          : t('accounts.errorGeneric'),
    );
  }

  const connectionActions: ConnectionActions = {
    onSync: (item) => void handleSync(item),
    onReconnect: (item) => void connect.start(item.id, item.accounts),
    onDisconnect: (item) => disconnecting.ask(item),
    syncBlocked: (item) => (syncUntil[item.id] ?? 0) > Date.now(),
  };

  function statusFor(account: Account): ReactNode {
    const item = connectionByAccount.get(account.id);
    if (item === undefined) return undefined;
    return (
      <ConnectionStatus
        item={item}
        actions={owner && isOwnerAccount(account) ? connectionActions : undefined}
      />
    );
  }

  function actionsFor(account: Account): AccountCardActions {
    const ownerRow = isOwnerAccount(account);
    const unmanaged = account.management === 'UNMANAGED';
    const open = account.status === 'OPEN';
    const canWrite = owner && ownerRow && open && !unmanaged;
    const importer = canImportBatches(account) && open && !unmanaged;
    return {
      ...(canWrite ? { edit: () => setDialog({ kind: 'edit', account }) } : {}),
      // Set / Edit starting balance (TMMAPP-0123 item 16): the edit form at the opening field.
      ...(canWrite
        ? {
            opening: {
              pending: account.openingPending,
              run: () => setDialog({ kind: 'edit', account, focusOpening: true }),
            },
          }
        : {}),
      ...(ownerRow && !unmanaged
        ? { permissions: () => void navigate(`/me/accounts/${account.id}?tab=permissions`) }
        : {}),
      ...(importer
        ? {
            import: {
              disabled: gate.loading,
              label: gate.loading
                ? t('common.loading')
                : gate.needsConsent
                  ? t('consent.chooseFirst')
                  : t('accounts.import'),
              run: () => {
                if (gate.loading) return;
                void navigate(
                  gate.needsConsent ? '/me/automation-help' : `/me/accounts/${account.id}/import`,
                );
              },
            },
          }
        : {}),
      // TMMAPP-0163 item 12: the owner answers a standing institution proposal from the kebab too.
      ...(ownerRow && account.institutionProposal != null
        ? {
            institution: {
              account,
              proposal: account.institutionProposal,
              onAnswered: () => void load(),
              onNotify: (message: string) => notify(message),
            },
          }
        : {}),
      ...(canWrite
        ? {
            close: () => {
              setCloseError(null);
              setClosing(account);
            },
          }
        : {}),
    };
  }

  function holdingActionsFor(holding: Holding): HoldingCardActions {
    if (!owner) return {};
    return {
      edit: () => setDialog({ kind: 'holding-edit', holding }),
      value: () => setDialog({ kind: 'holding-value', holding }),
      ...(holding.status === 'ACTIVE'
        ? { dispose: () => setDialog({ kind: 'holding-dispose', holding }) }
        : {}),
    };
  }

  async function handleClose() {
    if (closing === null || closeBusy) return;
    setCloseBusy(true);
    setCloseError(null);
    try {
      await accountsApi.close(closing.id);
      setClosing(null);
      notify(t('accounts.closedSnack'));
      await load();
    } catch (error) {
      setCloseError(
        t(
          isApiError(error) && error.code === ErrorCodes.ACCOUNT_CLOSED
            ? 'accounts.errorClosed'
            : 'accounts.errorGeneric',
        ),
      );
    } finally {
      setCloseBusy(false);
    }
  }

  async function refetchHoldingsAndNetWorth() {
    await Promise.all([loadHoldings(), loadNetWorth()]);
  }

  /** One category's account grid: the owner's open accounts sort; the add card stays last. */
  const accountGrid = (rows: Account[], type: ReorderableType | null, trailing: ReactNode) => (
    <SortableCardGrid
      items={rows}
      getId={(account) => account.id}
      getName={(account) => account.name}
      canReorder={owner && type !== null}
      isSortable={inAccountSet}
      onReorder={(ids) => (type === null ? Promise.resolve() : reorderAccounts(type, ids))}
      minCardWidth={ACCOUNT_MIN_CARD_WIDTH}
      trailing={trailing}
      renderCard={(account, handle) => (
        <AccountCard
          account={account}
          operating={operating}
          timeZone={timeZone}
          actions={actionsFor(account)}
          reorder={handle}
          status={statusFor(account)}
          reviewCount={
            reviewCounts === null
              ? undefined
              : (reviewCounts.get(account.id) ?? 0) + proposalReviewCount(account)
          }
        />
      )}
    />
  );

  const holdingGrid = (type: HoldingType, trailing: ReactNode) => (
    <SortableCardGrid
      items={(holdings ?? []).filter(
        (h) => h.type === type && (showDisposed || h.status === 'ACTIVE'),
      )}
      getId={(holding) => holding.id}
      getName={(holding) => holding.name}
      canReorder={owner}
      isSortable={inHoldingSet}
      onReorder={(ids) => reorderHoldings(type, ids)}
      minCardWidth={ACCOUNT_MIN_CARD_WIDTH}
      trailing={trailing}
      renderCard={(holding, handle) => (
        <HoldingCard
          holding={holding}
          accounts={accounts}
          currency={operating}
          actions={holdingActionsFor(holding)}
          reorder={handle}
        />
      )}
    />
  );

  function toolbarSwitch(label: string, checked: boolean, onChange: (checked: boolean) => void) {
    return (
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={checked}
            onChange={(_event, next) => onChange(next)}
            slotProps={{ input: { 'aria-label': label } }}
          />
        }
        label={label}
        sx={{ marginInlineStart: 0 }}
      />
    );
  }

  function addCard(action: ReturnType<typeof useSubscriberAction>, label: string, hint: string) {
    return (
      <>
        <AddNewCard
          label={label}
          hint={hint}
          minHeight={200}
          describedBy={action.descriptionId}
          onClick={action.run}
        />
        <SubscriberActionHint id={action.descriptionId} description={action.description} />
      </>
    );
  }

  const ownedCash = cash.filter(isOwnerAccount);
  const ownedRevolving = revolving.filter(isOwnerAccount);
  const ownedLoans = loans.filter(isOwnerAccount);
  const shared = visibleAccounts.filter((account) => !isOwnerAccount(account));
  const holdingsOf = (type: HoldingType) => (holdings ?? []).filter((h) => h.type === type);
  // A section with nothing in it isn't shown (§D 10) — counted over everything served, so a
  // section whose rows are only hidden (unmanaged, disposed) keeps its place and its toggle.
  const present: Record<SectionKey, boolean> = {
    cash: accounts.some((a) => a.type === 'CASH' && isOwnerAccount(a)),
    revolving: accounts.some((a) => a.type === 'REVOLVING' && isOwnerAccount(a)),
    shared: accounts.some((a) => !isOwnerAccount(a)),
    assets: holdingsOf('FIXED_ASSET').length > 0,
    tradable: holdingsOf('TRADABLE').length > 0,
    loans: accounts.some((a) => a.type === 'LOAN' && isOwnerAccount(a)),
  };
  // Nothing at all yet (a new subscriber, a guest): the Cash section with its first-account card.
  const nothing =
    holdingsView.status !== 'loading' && !Object.values(present).some((value) => value);
  if (nothing) present.cash = true;
  const totals: Record<SectionKey, number | null> = {
    cash: netWorth?.cashMinor ?? null,
    revolving: netWorth === null ? null : owed(netWorth.revolvingMinor),
    shared: null,
    assets: netWorth?.fixedAssetsMinor ?? null,
    tradable: netWorth?.tradableMinor ?? null,
    loans: netWorth === null ? null : owed(netWorth.loansMinor),
  };
  const shownHoldings = (type: HoldingType) =>
    holdingsOf(type).filter((h) => showDisposed || h.status === 'ACTIVE');

  function section(key: SectionKey, body: ReactNode, empty?: string | null) {
    if (!present[key]) return null;
    const headingId = `accounts-section-${key}`;
    const total = totals[key];
    return (
      <Box
        key={key}
        component="section"
        aria-labelledby={headingId}
        data-accounts-section={key}
        sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="h3" component="h2" id={headingId} sx={{ flex: 1, fontSize: 24 }}>
            {t(`accounts.section.${key}`)}
          </Typography>
          {total !== null && (
            <Typography
              component="span"
              data-section-total=""
              sx={{ fontSize: 14, color: tokens.text2, fontVariantNumeric: 'tabular-nums' }}
            >
              <MoneyParts minor={total} currency={operating} whole />
            </Typography>
          )}
        </Box>
        {empty !== undefined && empty !== null && (
          <Typography variant="body1" sx={{ maxWidth: 520, color: tokens.text2 }}>
            {empty}
          </Typography>
        )}
        {body}
      </Box>
    );
  }

  const holdingsSection = (key: 'assets' | 'tradable') => {
    const type: HoldingType = key === 'assets' ? 'FIXED_ASSET' : 'TRADABLE';
    const action = key === 'assets' ? addAsset : addTradable;
    return section(
      key,
      holdingGrid(
        type,
        addCard(
          action,
          t(key === 'assets' ? 'accounts.addCard.assets' : 'accounts.addCard.tradable'),
          t(key === 'assets' ? 'accounts.addCardHint.assets' : 'accounts.addCardHint.tradable'),
        ),
      ),
      shownHoldings(type).length === 0
        ? t(key === 'assets' ? 'holdings.emptyAssets' : 'holdings.emptyTradable')
        : null,
    );
  };

  const sections = [
    section(
      'cash',
      accountGrid(
        ownedCash,
        'CASH',
        firstAccount
          ? addCard(addCash, t('accounts.addFirst'), t('accounts.addFirstHint'))
          : addCard(addCash, t('accounts.addCard.cash'), t('accounts.addCardHint.cash')),
      ),
      // A person with no account yet (a new subscriber lands here, TMMAPP-0116): the add card
      // speaks for the first account and needs no empty line beside it.
      ownedCash.length === 0 && !firstAccount ? t('accounts.emptyCash') : null,
    ),
    section(
      'revolving',
      accountGrid(
        ownedRevolving,
        'REVOLVING',
        addCard(addRevolving, t('accounts.addCard.revolving'), t('accounts.addCardHint.revolving')),
      ),
      ownedRevolving.length === 0 ? t('accounts.emptyRevolving') : null,
    ),
    section('shared', accountGrid(shared, null, null)),
    holdingsSection('assets'),
    holdingsSection('tradable'),
    section(
      'loans',
      accountGrid(
        ownedLoans,
        'LOAN',
        addCard(addLoan, t('accounts.addCard.loans'), t('accounts.addCardHint.loans')),
      ),
      ownedLoans.length === 0 ? t('accounts.emptyLoans') : null,
    ),
  ];
  const showToolbar = unmanagedCount > 0 || (holdings ?? []).length > 0;

  const meta = (
    <Stack
      direction="row"
      data-accounts-meta=""
      sx={{ alignItems: 'center', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5 }}
    >
      <Link
        component="button"
        type="button"
        variant="body2"
        aria-describedby={helpLink.descriptionId}
        onClick={helpLink.run}
      >
        {t('consent.title')}
      </Link>
      <SubscriberActionHint id={helpLink.descriptionId} description={helpLink.description} />
      {/* Saved import formats are managed here, never from inside the import dialog (item 1). */}
      {subscriber && (
        <Link component={RouterLink} to="/me/accounts/profiles" variant="body2">
          {t('imports.profiles')}
        </Link>
      )}
      {unallocatedCount > 0 &&
        (cash.length > 0 ? (
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={(event) => handleNeedsClick(event)}
          >
            {t('accounts.tileNeedsAllocation', { count: unallocatedCount })}
          </Link>
        ) : (
          <Typography variant="body2">
            {t('accounts.tileNeedsAllocation', { count: unallocatedCount })}
          </Typography>
        ))}
    </Stack>
  );

  return (
    <>
      {header(
        netWorthView.status === 'ready' ? (
          <NetWorthCard
            variant="headline"
            netWorth={netWorthView.data}
            currency={operating}
            openingPendingAccountIds={[
              ...new Set([
                ...netWorthView.data.openingPendingAccountIds,
                ...summary.openingPendingAccountIds,
              ]),
            ]}
          />
        ) : netWorthView.status === 'loading' ? (
          <Skeleton variant="rounded" width={160} height={64} />
        ) : null,
        meta,
      )}

      {help !== null && gate.catalog !== null && (
        <HowWeHelpDialog
          catalog={gate.catalog}
          hideFor={help === 'arrival' ? me.user.id : undefined}
          onClose={() => setHelp(null)}
          // The full dialog writes its catalogue only on Save, so the gate reloads once, here.
          onSaved={() => {
            const continueConnect = help === 'connect';
            setHelp(null);
            void gate.reload().then(() => {
              // Opened from "Connect a bank": saving continues straight into the connection.
              if (continueConnect) void connect.start();
            });
          }}
        />
      )}

      {me.deletionRequested !== null && (
        <DeletionPendingAlert until={me.deletionRequested.coolingOffUntil} settingsLink />
      )}

      {netWorthView.status === 'error' && (
        <ErrorState
          variant="section"
          error={blockError(netWorthView)}
          what={t('errors.what.netWorth')}
          onRetry={() => void loadNetWorth()}
        />
      )}

      {unmapped.map(({ item, account }) => (
        <Alert
          key={item.id}
          severity="info"
          data-unmapped-banner=""
          action={
            <Button color="inherit" size="small" onClick={() => setMapTarget({ item, account })}>
              {t('connections.mapNow')}
            </Button>
          }
        >
          {t('connections.finishSetup', {
            bank: item.institutionName.length > 0 ? item.institutionName : t('connections.bank'),
          })}
        </Alert>
      ))}

      {showToolbar && (
        <Stack
          direction="row"
          data-accounts-toolbar=""
          sx={{ alignItems: 'center', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5 }}
        >
          {unmanagedCount > 0 &&
            toolbarSwitch(t('accounts.showUnmanaged'), showUnmanaged, handleShowUnmanaged)}
          {!showUnmanaged && unmanagedCount > 0 && (
            <Typography variant="caption" sx={{ color: tokens.text2 }}>
              {t('accounts.unmanagedHidden', { count: unmanagedCount })}
            </Typography>
          )}
          {(holdings ?? []).length > 0 &&
            toolbarSwitch(t('holdings.showDisposed'), showDisposed, setShowDisposed)}
        </Stack>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
        {sections}
        {holdingsView.status === 'loading' && <Skeleton variant="rounded" height={200} />}
        {holdingsView.status === 'error' && (
          <ErrorState
            variant="section"
            error={blockError(holdingsView)}
            what={t('errors.what.holdings')}
            onRetry={() => void loadHoldings()}
          />
        )}
      </Box>

      <Typography
        variant="caption"
        component="p"
        sx={{ maxWidth: 760, lineHeight: 1.7, color: tokens.textLabel('bg') }}
      >
        {t('accounts.identityNote')}
      </Typography>

      <ObjectMenu
        title={t('accounts.newAccount')}
        anchorEl={addMenu?.anchor ?? null}
        open={addMenu !== null}
        onClose={() => setAddMenu(null)}
      >
        <MenuItem
          onClick={() => {
            const type = addMenu?.type;
            setAddMenu(null);
            setDialog({ kind: 'create', ...(type !== undefined ? { type } : {}) });
          }}
        >
          {t('accounts.addManually')}
        </MenuItem>
        {!connect.unavailable && (
          <MenuItem
            onClick={() => {
              setAddMenu(null);
              startConnect();
            }}
          >
            {t('connections.connect')}
          </MenuItem>
        )}
      </ObjectMenu>

      <ObjectMenu
        title={t('accounts.addMenu')}
        anchorEl={addAnyMenu}
        open={addAnyMenu !== null}
        onClose={() => setAddAnyMenu(null)}
      >
        {(
          [
            ['CASH', 'accounts.addCard.cash'],
            ['REVOLVING', 'accounts.addCard.revolving'],
            ['LOAN', 'accounts.addCard.loans'],
          ] as const
        ).map(([type, label]) => (
          <MenuItem
            key={type}
            onClick={() => {
              setAddAnyMenu(null);
              setDialog({ kind: 'create', type });
            }}
          >
            {t(label)}
          </MenuItem>
        ))}
        <MenuItem
          onClick={() => {
            setAddAnyMenu(null);
            setDialog({ kind: 'holding-create', type: 'FIXED_ASSET' });
          }}
        >
          {t('accounts.addCard.assets')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAddAnyMenu(null);
            setDialog({ kind: 'holding-create', type: 'TRADABLE' });
          }}
        >
          {t('accounts.addCard.tradable')}
        </MenuItem>
        {!connect.unavailable && (
          <MenuItem
            onClick={() => {
              setAddAnyMenu(null);
              startConnect();
            }}
          >
            {t('connections.connect')}
          </MenuItem>
        )}
      </ObjectMenu>

      <Menu
        anchorEl={allocMenu}
        open={allocMenu !== null}
        onClose={() => setAllocMenu(null)}
        slotProps={{ list: { 'aria-label': t('accounts.needsAllocationMenu') } }}
      >
        {cash.map((account) => (
          <MenuItem key={account.id} onClick={() => openUnallocated(account)}>
            {account.name}
          </MenuItem>
        ))}
      </Menu>

      {connect.unavailable && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" size="small" onClick={() => connect.retry()}>
              {t('common.retry')}
            </Button>
          }
        >
          {t('connections.linkUnavailable')}
        </Alert>
      )}
      {connect.mapping !== null && (
        <MapAccountsDialog
          mapping={connect.mapping}
          operatingCurrency={operating}
          onClose={() => connect.dismissMapping()}
          onConnected={async () => {
            connect.dismissMapping();
            notify(t('connections.connected'));
            await load();
          }}
          onSessionExpired={connect.expireSession}
        />
      )}
      {mapTarget !== null && (
        <MapExistingDialog
          item={mapTarget.item}
          account={mapTarget.account}
          owned={accounts.filter(
            (account) => isOwnerAccount(account) && account.status !== 'CLOSED',
          )}
          takenIds={new Set(connectionByAccount.keys())}
          onClose={() => setMapTarget(null)}
          onMapped={async () => {
            setMapTarget(null);
            notify(t('connections.mapped'));
            await load();
          }}
        />
      )}
      {disconnecting.target !== null && (
        <ConfirmDialog
          title={t('connections.disconnectTitle', {
            institution: disconnecting.target.institutionName,
          })}
          consequence={t('connections.disconnectConfirm')}
          confirmLabel={t('connections.disconnect')}
          destructive
          busy={disconnecting.busy}
          error={disconnecting.error}
          onConfirm={() => void handleDisconnect()}
          onCancel={disconnecting.cancel}
        />
      )}
      {dialog?.kind === 'create' && (
        <AccountFormDialog
          operatingCurrency={operating}
          {...(dialog.type !== undefined ? { initialType: dialog.type } : {})}
          onClose={() => setDialog(null)}
          // The dialog stays open on its "{name} is ready" body (TMMAPP-0123 item 4).
          onSaved={async () => {
            await load();
          }}
          onImport={(account) => {
            setDialog(null);
            // The same consent gate as every import entry (TMMAPP-0124 item 14), fail-closed: an
            // unreviewed (or still loading) automation choice goes to automation help first.
            void navigate(
              gate.needsConsent ? '/me/automation-help' : `/me/accounts/${account.id}/import`,
            );
          }}
        />
      )}
      {dialog?.kind === 'edit' && (
        <AccountFormDialog
          operatingCurrency={operating}
          account={dialog.account}
          focusOpening={dialog.focusOpening === true}
          onClose={() => setDialog(null)}
          // An edit closes itself; a switch to managed stays open to offer the import (TMMAPP-0139).
          onSaved={async () => {
            await load();
          }}
          onImport={(account) => {
            setDialog(null);
            void navigate(
              gate.needsConsent ? '/me/automation-help' : `/me/accounts/${account.id}/import`,
            );
          }}
        />
      )}
      {closing !== null && (
        <ConfirmDialog
          title={t('accounts.closeTitle', { name: closing.name })}
          consequence={t('accounts.closeBody')}
          confirmLabel={t('accounts.close')}
          destructive
          busy={closeBusy}
          error={closeError}
          onConfirm={() => void handleClose()}
          onCancel={() => setClosing(null)}
        />
      )}
      {(dialog?.kind === 'holding-create' || dialog?.kind === 'holding-edit') && (
        <HoldingEditorDialog
          type={dialog.kind === 'holding-create' ? dialog.type : dialog.holding.type}
          {...(dialog.kind === 'holding-edit' ? { holding: dialog.holding } : {})}
          accounts={accounts}
          operatingCurrency={operating}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            notify(t('holdings.saved'));
            await refetchHoldingsAndNetWorth();
          }}
        />
      )}
      {dialog?.kind === 'holding-value' && (
        <ValuationDialog
          holding={dialog.holding}
          operatingCurrency={operating}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            notify(t('holdings.valued'));
            await refetchHoldingsAndNetWorth();
          }}
        />
      )}
      {dialog?.kind === 'holding-dispose' && (
        <DisposeHoldingDialog
          holding={dialog.holding}
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await holdingsApi.dispose(dialog.holding.id);
            setDialog(null);
            notify(t('holdings.disposedSnack'));
            await refetchHoldingsAndNetWorth();
          }}
        />
      )}
    </>
  );
}
